import { useCallback } from 'react'

/**
 * A generic hook that provides handlers for different input types.
 * It accepts a generic type `T` representing your state object.
 *
 * @param onValueChange - A callback to update the state.
 * @returns An object containing handlers for inputs, selects, checkboxes, etc.
 */
export function useInputHandlers<T extends Record<string, any>>(
  onValueChange: (updater: (prev: T) => T) => void
) {
  // Handler for input elements (e.g., <input />)
  const handleInputChange = useCallback(
    <K extends keyof T>(key: K, parseValue?: (value: string) => T[K]) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value
        const value = parseValue ? parseValue(rawValue) : (rawValue as unknown as T[K])
        onValueChange(prev => ({
          ...prev,
          [key]: value
        }))
      },
    [onValueChange]
  )

  // Handler for select elements (e.g., <select />)
  const handleSelectChange = useCallback(
    <K extends keyof T>(key: K, parseValue?: (value: string) => T[K]) =>
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const rawValue = event.target.value
        const value = parseValue ? parseValue(rawValue) : (rawValue as unknown as T[K])
        onValueChange(prev => ({
          ...prev,
          [key]: value
        }))
      },
    [onValueChange]
  )

  // Handler for checkbox elements (e.g., <input type="checkbox" />)
  const handleCheckboxChange = useCallback(
    <K extends keyof T>(key: K) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked as unknown as T[K]
        onValueChange(prev => ({
          ...prev,
          [key]: value
        }))
      },
    [onValueChange]
  )

  // Handler for inputs/selects using event.target.name as the key
  const handleNamedInputChange = useCallback(
    (parseValue?: (key: keyof T, value: string) => T[keyof T]) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const key = event.target.name as keyof T
        const rawValue = event.target.value
        const value = parseValue ? parseValue(key, rawValue) : (rawValue as unknown as T[keyof T])
        onValueChange(prev => ({
          ...prev,
          [key]: value
        }))
      },
    [onValueChange]
  )

  // Handler to toggle boolean values
  const toggleBooleanValue = useCallback(
    <K extends keyof T>(key: K) =>
      () => {
        onValueChange(prev => ({
          ...prev,
          [key]: !(prev[key] as unknown as boolean) as unknown as T[K]
        }))
      },
    [onValueChange]
  )

  const handleValueChange = useCallback(
    <I, K extends keyof T>(key: K, parseValue?: (value: I) => T[K]) =>
      (value: I) => {
        const parsedValue = parseValue ? parseValue(value) : (value as T[K])
        onValueChange(prev => ({
          ...prev,
          [key]: parsedValue
        }))
      },
    [onValueChange]
  )

  return {
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleNamedInputChange,
    toggleBooleanValue,
    handleValueChange
  }
}
